import cloneDeep from 'lodash/cloneDeep'
import {resolveStepStatus, STEP_STATUS_ICON} from '@/util/menu/stepStatus'

export default {
    computed: {
        getRegistrationSteps() {
            return this.$store.getters['registration/getRegistrationSteps']
        },
        stepsValidationSections() {
            return this.$store.state.registration.stepsValidationSections
        },
        stepsBefore() {
            let index = this.cloneSteps().findIndex((step) => step.key === this.activeStep?.key)
            return this.cloneSteps().splice(0, index)
        },
        currentStep() {
            let index = this.cloneSteps().findIndex((step) => step.key === this.activeStep?.key)
            return this.cloneSteps()[index]
        },
        stepsAfter() {
            let index = this.cloneSteps().findIndex((step) => step.key === this.activeStep?.key)
            return this.cloneSteps().splice(index + 1)
        },
        visibleStepsBefore() {
            return this.stepsBefore.filter((step) => !step.hidden)
        },
        visibleStepsAfter() {
            return this.stepsAfter.filter((step) => !step.hidden)
        },
        // Visible steps grouped for menu rendering: a normal step stays a 'step', while the
        // exploded question steps collapse into a 'group' carrying their sub-page steps.
        // A question step with only a single visible sub-page (i.e. no effective page break)
        // collapses back to a plain 'step' so the menu shows just its top-level item — no
        // confusing "1 / 1" sub-page entry.
        visibleStepGroups() {
            const groups = []
            for (const step of this.getRegistrationSteps) {
                if (step.hidden) continue
                if (step.parent) {
                    const last = groups[groups.length - 1]
                    if (last && last.type === 'group' && last.parent === step.parent) {
                        last.steps.push(step)
                    } else {
                        groups.push({type: 'group', parent: step.parent, steps: [step]})
                    }
                } else {
                    groups.push({type: 'step', step})
                }
            }
            return groups.map((entry) =>
                entry.type === 'group' && entry.steps.length === 1
                    ? {type: 'step', step: entry.steps[0]}
                    : entry
            )
        },
        activeEntryIndex() {
            return this.visibleStepGroups.findIndex((entry) =>
                entry.type === 'group'
                    ? entry.steps.some((step) => step.key === this.activeStep?.key)
                    : entry.step.key === this.activeStep?.key
            )
        },
        entriesBefore() {
            const index = this.activeEntryIndex
            return index < 0 ? [] : this.visibleStepGroups.slice(0, index)
        },
        currentEntry() {
            const index = this.activeEntryIndex
            return index < 0 ? null : this.visibleStepGroups[index]
        },
        entriesAfter() {
            const index = this.activeEntryIndex
            return index < 0 ? [] : this.visibleStepGroups.slice(index + 1)
        },
        activeEntryDomId() {
            return this.currentEntry ? this.entryDomId(this.currentEntry) : null
        },
        activeStep() {
            const steps = this.getRegistrationSteps
            if (!steps?.length) return undefined

            if (this.stepName) {
                return steps.find((step) => step.key === this.stepName)
                    || steps.find((step) => step.name === this.stepName)
            }

            // Most specific route segment whose name is contained in the current route.
            const matching = [...steps]
                .sort((a, b) => b.name.length - a.name.length)
                .filter((step) => this.$route.name.includes('registration-id-' + step.name))
            if (!matching.length) return undefined

            const segment = matching[0].name
            const candidates = steps.filter((step) => step.name === segment)
            if (candidates.length === 1) return candidates[0]

            // Exploded route: pick the candidate matching the active sub-page index.
            const sub = this.getActiveSubPageIndex(segment, candidates.length)
            return candidates.find((step) => step.subIndex === sub) || candidates[0]
        }
    },
    methods: {
        nextStep() {
            this.redirectUserToEnabledStep(true)
        },
        previousStep() {
            this.redirectUserToEnabledStep(false, true)
        },
        cloneSteps() {
            return cloneDeep(this.getRegistrationSteps)
        },
        stepStatus(step) {
            return resolveStepStatus(
                step,
                this.activeStep?.key,
                this.$store.state.registration.stepsValidationState
            )
        },
        stepStatusIcon(step) {
            return STEP_STATUS_ICON[this.stepStatus(step)]
        },
        menuItemComponent(name) {
            return 'MenuItem' + name.charAt(0).toUpperCase() + name.slice(1)
        },
        entryDomId(entry) {
            return entry.type === 'group' ? 'entry-' + entry.parent : 'entry-' + entry.step.key
        },
        getActiveSubPageIndex(parent, count) {
            const raw = this.$store.state.registration.activeSubPage[parent] ?? 0
            return Math.min(Math.max(raw, 0), Math.max(count - 1, 0))
        },
        goToStep(step) {
            if (!step) return
            // Remember which slice is active for the parent route.
            if (step.parent) {
                this.$store.commit('registration/setActiveSubPage', {parent: step.parent, index: step.subIndex})
            }
            // Same route segment (another sub-page of the current step) → no navigation,
            // the active sub-index change drives the re-render.
            if (step.name === this.activeStep?.name) return
            this.$router.replace({
                path: this.$localePath({name: 'registration-id-' + step.name}),
                query: this.$route.query
            })
        },
        redirectUserToEnabledStep(nextStep = false, previousStep = false) {
            const steps = this.getRegistrationSteps
            const activeStepIndex = steps.findIndex((step) => step.key === this.activeStep?.key)
            let targetStep = null
            // First look ahead, maybe this step is just disabled because it is unnecessary for this user
            for (let i = activeStepIndex + (nextStep ? 1 : 0); i < steps.length; i++) {
                if (!steps[i].disabled && !steps[i].hidden) {
                    targetStep = steps[i]
                    break
                }
            }
            if (!targetStep || previousStep) {
                // Now lets look back, if no step ahead is active, we have some errors to fix
                for (let i = activeStepIndex - (previousStep ? 1 : 0); i >= 0; i--) {
                    if (!steps[i].disabled && !steps[i].hidden) {
                        targetStep = steps[i]
                        break
                    }
                }
            }
            if (!targetStep || targetStep.key === this.activeStep?.key) {
                return
            }
            this.goToStep(targetStep)
        }
    }
}
